// ── Plain-document mode (client mirror of server/src/services/merchantMode.ts) ───────────────────
// Owner 2026-10-03 (decision C = A): a merchant outside Thailand issues plain quotations, invoices
// and receipts only — no Thai VAT, no withholding, no Thai titles. Each line carries the tax rate the
// merchant types; the document prints a plain "Tax" row. The server decides; this only mirrors it.
import { regionOf } from '../../utils/region';

/** Thai tax documents unless the profile says otherwise — an older API that sends nothing reads as Thai.
 *  The MERCHANT's current mode: what a NEW document issues as, which tabs exist. */
export const thaiModeOf = (profile: any): boolean => profile?.thaiTaxMode !== false;

/** A stored DOCUMENT's own mode (its `tax_mode`, fixed when it was issued — server merchantMode.ts docPlain).
 *  Every per-document rule follows it: a Thai document stays Thai after the merchant leaves Thailand. */
export const docPlainOf = (row: any): boolean => String(row?.tax_mode ?? '').trim() === 'plain';

const r2 = (n: number): number => Math.round(n * 100) / 100;

/** A typed tax rate the server will accept: blank (= 0) or a number from 0 to 100. */
export const plainRateOk = (v: any): boolean => {
  if (v === undefined || v === null || String(v).trim() === '') return true;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 100;
};

/** Document totals for a plain document. MUST mirror calcPlainItems() in server/src/routes/portalDocs.ts
 *  (rate added on top of each line, every line and running total rounded to 2dp, no withholding). */
export function plainLineTotals(items: any[]): { preVat: number; vat: number; wht: number; total: number; net: number } {
  let preVat = 0, vat = 0;
  for (const it of items) {
    const pv = r2((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0));
    const rate = Number(it.taxRate) || 0;
    const v = rate ? r2(pv * rate / 100) : 0;
    preVat = r2(preVat + pv); vat = r2(vat + v);
  }
  const total = r2(preVat + vat);
  return { preVat, vat, wht: 0, total, net: total };
}

/** Currencies offered to a plain merchant: its own first, then the usual list, then every pack's. */
export function plainCurrencies(own: string, base: string[], packs?: string[]): string[] {
  return Array.from(new Set([own, ...base, ...(packs || [])].map(c => String(c || '').trim().toUpperCase()).filter(c => /^[A-Z]{3}$/.test(c))));
}

/** The merchant's own calendar day (YYYY-MM-DD) in its zone — the workspace's when it has none. */
export function merchantToday(companyId?: string | null): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: regionOf(companyId).timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/** The usual document / bank currencies (the list the portal has always offered). */
export const COMMON_CURRENCIES = ['THB', 'USD', 'EUR', 'GBP', 'SGD', 'AUD', 'JPY', 'CNY', 'HKD', 'CHF'];
